import type { ChangeEventHandler } from 'react';
import type {
  Media,
  MediaHydrated,
} from '../../lib/flotiq-client/types/api-response';
import type { UserLocalStorage } from '../../types/localStorage';

import { use, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import Tabs from '../../components/Tabs/Tabs';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';

import useMediaUpload from '../../hooks/api/useMediaUpload';
import { useSpaceConstraint } from '../../hooks/api/useSpaceConstraint';

import UserContext from '../../contexts/UserContext';

import PageLayout from '../../layout/PageLayout/PageLayout';
import MediaSecondaryTopar from './MediaSecondaryTopar/MediaSecondaryTopar';
import UnsplashMedia from './Subpages/UnsplashMedia';
import UserMedia from './Subpages/UserMedia';

const testId = 'media-library';

const MediaLibrary = () => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const { permissions } = use(UserContext);
  const canCreate = permissions.canCo(
    '_media',
    RolePermissions.PERMISSIONS_TYPES.CREATE,
  );

  const { onUpload } = useMediaUpload();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');

  const [selectedMedia, setSelectedMedia] = useState<
    Record<string, Media | MediaHydrated>
  >({});

  const selectMedia = useCallback(
    (media: Media | MediaHydrated) =>
      setSelectedMedia((prev) => {
        const newSelectedMedia = { ...prev };
        if (newSelectedMedia[media.id]) delete newSelectedMedia[media.id];
        else newSelectedMedia[media.id] = media;
        return newSelectedMedia;
      }),
    [],
  );

  const clearSelection = useCallback(() => setSelectedMedia({}), []);

  const [mediaOnPageLimit, setMediaOnPageLimit] = useState(
    // We are using +`${x}` interpolation instead of +x
    // to prevent React from evaluating the entire expression
    // during a build.
    +`${getEnv('VITE_DEFAULT_MEDIA_LIMIT')}` || 12,
  );

  const { constraint: mediaSumSize, reload: reloadConstraint } =
    useSpaceConstraint('media-sum-size');

  const handleUpload = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (e) => {
      onUpload(e.target.files);
      if (inputRef.current) inputRef.current.value = '';
    },
    [onUpload],
  );

  const handleReloadMedia = useCallback(() => {
    reloadConstraint();
  }, [reloadConstraint]);

  const tabsContent = useMemo(() => {
    const tabsContent = [
      {
        label: (
          <span className={'text-sm! sm:text-base!'}>
            {t('Media.Tabs.UploadedFiles')}
          </span>
        ),
        key: 'UserMediaTab',
        render: () => (
          <UserMedia
            mediaOnPageLimit={mediaOnPageLimit}
            setMediaOnPageLimit={setMediaOnPageLimit}
            onUpload={onUpload}
            onRemove={handleReloadMedia}
            selectedMedia={selectedMedia}
            selectMedia={selectMedia}
            clearSelection={clearSelection}
            testId={testId}
            hasGridView
          />
        ),
      },
    ];
    const disabledUnsplash = getEnv('VITE_ENABLE_UNSPLASH') === 'false';
    if (!disabledUnsplash) {
      tabsContent.push({
        label: (
          <span className={'text-sm! sm:text-base!'}>
            {t('Media.Tabs.UnsplashFiles')}
          </span>
        ),
        key: 'UnsplashMediaTab',
        render: () => (
          <UnsplashMedia
            mediaOnPageLimit={mediaOnPageLimit}
            setMediaOnPageLimit={setMediaOnPageLimit}
            handleReloadMedia={handleReloadMedia}
            {...getTestProps(testId, 'unsplash', 'testId')}
          />
        ),
      });
    }

    return tabsContent;
  }, [
    t,
    mediaOnPageLimit,
    onUpload,
    selectedMedia,
    selectMedia,
    clearSelection,
    handleReloadMedia,
  ]);

  return (
    <PageLayout
      page="media"
      title={t('Global.MediaLibrary')}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={
        <TopbarButton
          label={
            <>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={handleUpload}
                accept={user?.data?.allowedMimetypes}
                multiple
                {...getTestProps(testId, 'global-file-input')}
              />
              {t('Media.AddFile')}
            </>
          }
          tooltip={t('Media.AddFileTooltip')}
          tooltipPlacement="leftBottom"
          additionalTooltipClasses={'hidden md:block'}
          as="label"
          actionDisallowed={!canCreate}
        />
      }
      secondaryTopbar={
        <MediaSecondaryTopar mediaSumSize={mediaSumSize} testId={testId} />
      }
      testId={testId}
      noPaddings
      fillHeight
    >
      <Tabs
        additionalContainerClasses="order-2 lg:order-0 px-5 lg:px-7 pt-5 md:pt-0"
        tabs={tabsContent}
        testId={testId}
        fillHeight
      />
    </PageLayout>
  );
};

export default MediaLibrary;
